import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import { Box, Link, Typography } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import { DocumentationViewItem } from 'store/documentation/model/types';
import { useDocumentationStore } from 'store/documentation';
import { useDirectoriesLookupMap } from 'store/documentation/hooks/useDirectoriesLookupMap';
import { Color } from 'shared/utils/colors';

const HOVER_DEBOUNCE_MS = 0;

interface Props {
  rowData: DocumentationViewItem;
}

export function DocumentationParentDirectoryCell({ rowData }: Props): ReactElement {
  const intl = useIntl();
  const directoriesMap = useDirectoriesLookupMap();
  const navigateToDirectory = useDocumentationStore.use.navigateToDirectory();
  const setHoveredDirectory = useDocumentationStore.use.setHoveredDirectory();
  const fetchHoveredBreadcrumbs = useDocumentationStore.use.fetchHoveredBreadcrumbs();
  const clearHoveredBreadcrumbs = useDocumentationStore.use.clearHoveredBreadcrumbs();
  
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cellRef = useRef<HTMLButtonElement>(null);

  const parentId = rowData.parentId ?? '';
  const isRoot = parentId === '';

  const parentDirectoryName = useMemo(() => {
    if (isRoot) {
      return formatTranslation(intl, CommonTranslationKeys.documentation);
    }
    const parentDirectory = directoriesMap.get(parentId);
    return parentDirectory?.name ?? '';
  }, [isRoot, parentId, directoriesMap, intl]);

  const handleNavigate = useCallback((): void => {
    navigateToDirectory(parentId, isRoot ? undefined : parentDirectoryName);
  }, [navigateToDirectory, parentId, isRoot, parentDirectoryName]);

  const getCellPosition = useCallback((): { rowTop: number; cellRight: number } | null => {
    if (!cellRef.current) return null;
    
    // Find the row element
    const rowElement = cellRef.current.closest('.MuiDataGrid-row');
    if (!rowElement) return null;
    
    // Find the breadcrumb container wrapper
    const positionedContainer = cellRef.current.closest('[data-breadcrumb-container]');
    if (!positionedContainer) return null;
    
    const containerRect = positionedContainer.getBoundingClientRect();
    const rowRect = rowElement.getBoundingClientRect();
    // Use the Link element itself (which wraps icon + text) to get content width
    const linkRect = cellRef.current.getBoundingClientRect();
    
    // Calculate top of the row and right edge of the folder name text relative to container
    return {
      rowTop: rowRect.top - containerRect.top,
      cellRight: linkRect.right - containerRect.left,
    };
  }, []);

  const handleMouseEnter = useCallback((): void => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    hoverTimeoutRef.current = setTimeout(() => {
      const position = getCellPosition();
      setHoveredDirectory(parentId, position?.rowTop ?? null, position?.cellRight ?? null);
      fetchHoveredBreadcrumbs(parentId);
    }, HOVER_DEBOUNCE_MS);
  }, [parentId, setHoveredDirectory, fetchHoveredBreadcrumbs, getCellPosition]);

  const handleMouseLeave = useCallback((): void => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    // Don't clear breadcrumbs here - let the bar handle its own mouseLeave
  }, []);

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  if (!parentDirectoryName) {
    return <Typography variant='body2'>-</Typography>;
  }

  return (
    <Link
      ref={cellRef}
      component='button'
      type='button'
      variant='body2'
      underline='none'
      onClick={handleNavigate}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        textAlign: 'left',
        color: 'text.primary',
        overflow: 'hidden',
        maxWidth: '100%',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
    >
      <FolderIcon
        sx={{
          fontSize: 20,
          color: Color.iconGray,
          flexShrink: 0,
        }}
      />
      <Box
        component='span'
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {parentDirectoryName}
      </Box>
    </Link>
  );
}
